/**
 * LeverRow (J6, Wave 3) — one autonomy lever line: name + server-resolved
 * current tier, the L1..L5 toggle row (locked options disabled with a lock
 * icon), the server lock reasons rendered once per distinct reason, and the
 * honest per-lever note (routing "enforces nothing yet", experiments G3).
 *
 * Frontend never derives verdicts: selected = server `currentTier`; a tier is
 * locked/selectable ONLY per the server `tierOptions`; lock reasons and notes
 * are server strings verbatim (AC-013). While a save is in flight anywhere on
 * the page the toggles are disabled (busy lock — one action at a time,
 * AC-EH1 refetch renders the server state afterwards).
 *
 * Composition per .design/autonomy-facade/mockups/autonomy-page.html.
 */

import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import BlockIcon from '@mui/icons-material/Block';
import LockIcon from '@mui/icons-material/Lock';
import { AUTONOMY_TEST_IDS } from '../testIds';
import type { AutonomyLever, AutonomyLeverState, AutonomyTier } from '../types/autonomy.types';
import type { AutonomyBusyKey } from '../hooks/useAutonomySettings';
import {
  AUTONOMY_TIER_SEQUENCE,
  compareTiers,
  formatTierRange,
  isTierLocked,
  isTierSelectable,
  lockReasonGroups,
  tierToggleLabel,
} from '../utils/tiers';

export interface LeverRowProps {
  lever: AutonomyLeverState;
  /** Lever (or 'killSwitch') with a save in flight — disables the toggles. */
  busyLever: AutonomyBusyKey | null;
  /** Imperative save + refetch (useAutonomySettings.setLeverTier). */
  onTierChange: (lever: AutonomyLever, tier: AutonomyTier) => void;
}

/** Lock glyph: above the server ceiling = permanent cap, else approval lock. */
function LockGlyph({ aboveCeiling }: { aboveCeiling: boolean }) {
  return aboveCeiling
    ? <BlockIcon sx={{ fontSize: 14 }} aria-label="Permanently capped" />
    : <LockIcon sx={{ fontSize: 14 }} aria-label="Locked" />;
}

export const LeverRow: React.FC<LeverRowProps> = ({ lever, busyLever, onTierChange }) => {
  const busy = busyLever !== null;
  const saving = busyLever === lever.lever;
  const reasonGroups = lockReasonGroups(lever);
  // Server ceiling below L5 renders the permanent-cap hint next to the
  // current tier ("· capped at L4 permanently" on pricing, per the mock).
  const capped = compareTiers(lever.ceiling, 'L5') < 0;

  const handleChange = (_event: React.MouseEvent<HTMLElement>, value: AutonomyTier | null) => {
    if (!value || value === lever.currentTier) return;
    onTierChange(lever.lever, value);
  };

  return (
    <Box
      data-testid={AUTONOMY_TEST_IDS.lever(lever.lever)}
      sx={{
        px: 2.5,
        py: 1.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* Head: lever name + server-resolved current tier (G8: label = enforced tier) */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
          {lever.lever}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          current: {tierToggleLabel(lever.currentTier)}
          {capped && ` · capped at ${lever.ceiling} permanently`}
        </Typography>
        {saving && <CircularProgress size={12} aria-label="Saving tier" />}
      </Box>

      {/* Tier toggle row — selected value is the SERVER-resolved tier */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={lever.currentTier}
        onChange={handleChange}
        aria-label={`${lever.lever} autonomy tier`}
      >
        {AUTONOMY_TIER_SEQUENCE.map((tier) => {
          const locked = isTierLocked(lever, tier);
          const selectable = isTierSelectable(lever, tier);
          return (
            <ToggleButton
              key={tier}
              value={tier}
              disabled={locked || !selectable || busy}
              data-testid={AUTONOMY_TEST_IDS.tierOption(lever.lever, tier)}
              sx={{ whiteSpace: 'nowrap', gap: 0.75, textTransform: 'none' }}
            >
              {locked && <LockGlyph aboveCeiling={compareTiers(tier, lever.ceiling) > 0} />}
              {tierToggleLabel(tier)}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>

      {/* Server lock reasons — each distinct reason once; ceiling reason first
          in warning color without a tier prefix (mock: pricing) */}
      {reasonGroups.length > 0 && (
        <Box data-testid={AUTONOMY_TEST_IDS.leverLockReason(lever.lever)}>
          {reasonGroups.map((group) => (
            <Typography
              key={`${group.aboveCeiling ? 'ceiling' : 'lock'}-${group.reason}`}
              variant="caption"
              component="div"
              sx={{
                mt: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: group.aboveCeiling ? 'warning.main' : 'text.secondary',
              }}
            >
              <Box component="span" sx={{ color: group.aboveCeiling ? 'warning.main' : 'text.disabled', display: 'inline-flex' }}>
                <LockGlyph aboveCeiling={group.aboveCeiling} />
              </Box>
              {group.aboveCeiling
                ? group.reason
                : `${formatTierRange(group.tiers)}: ${group.reason}`}
            </Typography>
          ))}
        </Box>
      )}

      {/* Honest per-lever note (server string verbatim) */}
      {lever.note && (
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ mt: 0.75, borderLeft: '3px solid', borderColor: 'divider', pl: 1 }}
        >
          {lever.note}
        </Typography>
      )}
    </Box>
  );
};

export default LeverRow;
